import React, { useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { redirect, useLoaderData } from "react-router";
import { IAPIResponse, IGameLog, ITournament } from "../../types/api";
import { getGame, getGameLogs } from "../../lib/api";
import { defaultDateFormat, isChildTournament } from "../../lib/utils";
import { useAppSelector } from "../../hooks/reduxHooks";
import Select from "react-select";
import {
    globalActions,
    selectAgeLevels,
} from "../../features/global/globalSlice";
import { store } from "../..";
import DatePicker from "react-datepicker";
import GameLogTable from "./GameLogTable";
import {
    selectSelectedTournament,
    tournamentManagementActions,
} from "../../features/tourneymgmt/tournamentManagementSlice";

interface Filters {
    gameDate?: Date;
    logDate?: Date;
    ageLevels: {
        options?: Array<string>;
        filter?: Array<string>;
    };
    action: {
        options?: Array<string>;
        filter?: Array<string>;
    };
    logCategory: {
        options?: Array<string>;
        filter?: Array<string>;
    };
    team?: string;
}

export const loader = async ({ params }: any) => {
    const { gameId } = params;

    let logs: IAPIResponse & { logs: IGameLog[] };
    let header: string;

    let tournaments = store.getState().tournamentmanagement.tournaments;
    let masterTournaments =
        store.getState().tournamentmanagement.masterTournaments;

    const tournamentsPromise = tournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getTournaments());

    const masterTournamentsPromise = masterTournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getMasterTournaments());

    await Promise.all([tournamentsPromise, masterTournamentsPromise]);

    tournaments = !tournaments.length
        ? store.getState().tournamentmanagement.tournaments
        : tournaments;
    masterTournaments = !masterTournaments.length
        ? store.getState().tournamentmanagement.masterTournaments
        : masterTournaments;

    if (gameId) {
        const { game } = await getGame(gameId);
        store.dispatch(
            tournamentManagementActions.setselectedTournamentId(
                game.tournament,
            ),
        );
        const tournament = selectSelectedTournament(
            store.getState(),
        ) as ITournament;
        const masterTournament = masterTournaments.find(
            t => t.id === tournament.parent,
        );

        logs = await getGameLogs(gameId);
        const home = game.home.name || "TBD";
        const visitor = game.visitor.name || "TBD";
        header = `${masterTournament?.name} ${tournament.ageLevel} - ${visitor} vs. ${home} Game Log`;
    } else {
        logs = await getGameLogs();
        header = "Game Logs";
    }

    const ageLevels = store.getState().global.ageLevels;

    const ageLevelsPromise = ageLevels.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getAgeLevels());

    await Promise.all([ageLevelsPromise]);

    return {
        gameId,
        logs,
        header,
    };
};

const GameLogs = (): React.JSX.Element => {
    const {
        logs: { logs },
        gameId,
    } = useLoaderData() as {
        gameId?: number;
        logs: IAPIResponse & { logs: IGameLog[] };
    };

    const ageLevels = useAppSelector(selectAgeLevels);
    const [filters, setFilters] = useState<Filters>({
        team: undefined,
        ageLevels: {
            options: Array.from(new Set(ageLevels.map(a => a.ageLevel))),
            filter: undefined,
        },
        action: {
            options: Array.from(new Set(logs.map(l => l.action))).sort(),
            filter: undefined,
        },
        gameDate: undefined,
        logDate: undefined,
        logCategory: {
            options: Array.from(new Set(logs.map(l => l.category))).sort(),
            filter: undefined,
        },
    });

    const gameLogs = logs
        .filter(l =>
            filters.team
                ? l.game.home.name
                      .toLowerCase()
                      .includes(filters.team.toLowerCase()) ||
                  l.game.visitor.name
                      .toLowerCase()
                      .includes(filters.team.toLowerCase())
                : true,
        )
        .filter(l =>
            filters.ageLevels.filter
                ? filters.ageLevels.filter.includes(l.game.ageLevel.ageLevel)
                : true,
        )
        .filter(l =>
            filters.action.filter
                ? filters.action.filter.includes(l.action)
                : true,
        )
        .filter(l =>
            filters.logCategory.filter
                ? filters.logCategory.filter.includes(l.category)
                : gameId
                  ? l.category !== "Umpire"
                  : true,
        )
        .filter(l => {
            return filters.logDate
                ? defaultDateFormat.format(new Date(l.date)) ===
                      defaultDateFormat.format(filters.logDate)
                : true;
        })
        .filter(l => {
            return filters.gameDate
                ? defaultDateFormat.format(new Date(l.game.date)) ===
                      defaultDateFormat.format(filters.gameDate)
                : true;
        });

    const umpireLogs = logs
        .filter(l =>
            filters.team
                ? l.game.home.name
                      .toLowerCase()
                      .includes(filters.team.toLowerCase()) ||
                  l.game.visitor.name
                      .toLowerCase()
                      .includes(filters.team.toLowerCase())
                : true,
        )
        .filter(l =>
            filters.ageLevels.filter
                ? filters.ageLevels.filter.includes(l.game.ageLevel.ageLevel)
                : true,
        )
        .filter(l =>
            filters.action.filter
                ? filters.action.filter.includes(l.action)
                : true,
        )
        .filter(l => (gameId ? l.category === "Umpire" : true))
        .filter(l => {
            return filters.logDate
                ? defaultDateFormat.format(new Date(l.date)) ===
                      defaultDateFormat.format(filters.logDate)
                : true;
        })
        .filter(l => {
            return filters.gameDate
                ? defaultDateFormat.format(new Date(l.game.date)) ===
                      defaultDateFormat.format(filters.gameDate)
                : true;
        });

    return (
        <section className="gameLogsContent" data-testid="gameLogsContent">
            {!gameId && (
                <section className="gameLogsFilters">
                    <Row>
                        <Col xs={12} sm={6} xl={4} className="my-2">
                            <h5>Age Level:</h5>
                            <Select
                                isSearchable={false}
                                isClearable={true}
                                isMulti={true}
                                options={filters.ageLevels.options?.map(
                                    (a, idx) => {
                                        return {
                                            value: idx,
                                            label: a,
                                        };
                                    },
                                )}
                                onChange={a => {
                                    setFilters(prev => ({
                                        ...prev,
                                        ageLevels: {
                                            ...prev.ageLevels,
                                            filter:
                                                a.length === 0
                                                    ? undefined
                                                    : a?.map(o => o.label),
                                        },
                                    }));
                                }}
                            />
                        </Col>
                        <Col xs={12} sm={6} xl={4} className="my-2">
                            <h5>Team:</h5>
                            <Form.Control
                                type="text"
                                placeholder="Team"
                                onChange={e => {
                                    setFilters(prev => ({
                                        ...prev,
                                        team:
                                            e.target.value.trim() === ""
                                                ? undefined
                                                : e.target.value.trim(),
                                    }));
                                }}
                            />
                        </Col>
                        <Col xs={12} sm={6} xl={2} className="my-2">
                            <h5>Game Date:</h5>
                            <DatePicker
                                selected={
                                    filters.gameDate
                                        ? new Date(filters.gameDate)
                                        : undefined
                                }
                                onChange={d =>
                                    setFilters(prev => ({
                                        ...prev,
                                        gameDate: d || undefined,
                                    }))
                                }
                                isClearable
                                showMonthDropdown
                                showYearDropdown
                                dateFormat="MMMM d, yyyy"
                                dropdownMode="select"
                                className="form-control"
                            />
                        </Col>
                        <Col xs={12} sm={6} xl={2} className="my-2">
                            <h5>Log Date:</h5>
                            <DatePicker
                                selected={
                                    filters.logDate
                                        ? new Date(filters.logDate)
                                        : undefined
                                }
                                onChange={d =>
                                    setFilters(prev => ({
                                        ...prev,
                                        logDate: d || undefined,
                                    }))
                                }
                                isClearable
                                showMonthDropdown
                                showYearDropdown
                                dateFormat="MMMM d, yyyy"
                                dropdownMode="select"
                                className="form-control"
                            />
                        </Col>
                        <Col xs={12} sm={6} className="my-2">
                            <h5>Log Action:</h5>
                            <Select
                                isSearchable={false}
                                isClearable={true}
                                isMulti={true}
                                options={filters.action.options?.map(
                                    (a, idx) => {
                                        return {
                                            value: idx,
                                            label: a,
                                        };
                                    },
                                )}
                                onChange={a => {
                                    setFilters(prev => ({
                                        ...prev,
                                        action: {
                                            ...prev.action,
                                            filter:
                                                a.length === 0
                                                    ? undefined
                                                    : a?.map(o => o.label),
                                        },
                                    }));
                                }}
                            />
                        </Col>
                        <Col xs={12} sm={6} className="my-2">
                            <h5>Log Category:</h5>
                            <Select
                                isSearchable={false}
                                isClearable={true}
                                isMulti={true}
                                options={filters.logCategory.options?.map(
                                    (a, idx) => {
                                        return {
                                            value: idx,
                                            label: a,
                                        };
                                    },
                                )}
                                onChange={a => {
                                    setFilters(prev => ({
                                        ...prev,
                                        logCategory: {
                                            ...prev.logCategory,
                                            filter:
                                                a.length === 0
                                                    ? undefined
                                                    : a?.map(o => o.label),
                                        },
                                    }));
                                }}
                            />
                        </Col>
                    </Row>
                </section>
            )}
            {gameId && <h3>Game History</h3>}
            <GameLogTable data={gameLogs} />
            {gameId && <h3>Umpire History</h3>}
            {gameId && <GameLogTable data={umpireLogs} />}
        </section>
    );
};

export default GameLogs;
